<template>
  <div class="recording">
    <div class="deleting-box" v-if="deleting">
      <Loader :small="true" color="white"/>
    </div>
    <div class="limiter" v-if="!deleting">
      <div class="watch-box">
        <div class="circle" title="Watch" @click="onWatch">
          <font-awesome-icon :icon="['fas', 'play']" class="icon" title="Watch"/>
        </div>
      </div>
      <div class="owner-box">{{ data.owner }}</div>
      <div class="time-box">{{ getTime }}</div>
      <div class="date-box">{{ getDate }}</div>
      <div class="share-box">
        <font-awesome-icon :icon="['fas', 'share-alt']" class="icon" title="Share" @click="onShare"/>
      </div>
      <div class="delete-box">
        <font-awesome-icon :icon="['fas', 'trash']" class="icon" title="Delete" @click="onDelete"/>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import alert from '@/lib/alert'
import iframe from '@/lib/iframe'
import swal from 'sweetalert'
import Loader from '@/components/Loader'

export default {
  name: 'RecordingLine',
  props: {
    data: Object
  },
  data() {
    return {
      deleting: false
    }
  },
  components: {
    Loader
  },
  methods: {
    onWatch() {
      iframe.open(`${window.location.origin}/watch?dialog_id=${this.data._id}`);
    },
    onShare() {
      this.$emit('share', { title: 'Share Recording', link: `${window.location.origin}/recording?dialog_id=${this.data._id}` })
    },
    onDelete() {
      swal({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this recording!",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      })
      .then((willDelete) => {
        if (willDelete) {
          this.deleting = true;
          axios({
            method: 'post',
            url: `${process.env.VUE_APP_SERVER_URL}deleteDialog`,
            headers: {
              Authorization: 'Bearer ' + localStorage.getItem('token') 
            },
            data: {
              dialogId: this.data._id,
              exerciseId: this.data.exerciseId
            }
          }).then(() => {
            this.$emit('delete', this.data._id);
            swal("Recording successfully deleted!", {
              icon: "success",
              timer: 2000,
            });
          }).catch(err => {
            alert.showError(err.message);
          })
        } 
      });
    }
  },
  computed: {
    getDate() {
      const oneDay = 24 * 60 * 60 * 1000;
      let currentDate = new Date();
      let recordDate = new Date(this.data.created);
      
      let dayDiff = Math.round(Math.abs((recordDate - currentDate) / oneDay));
      
      if(dayDiff == 0) {
        return `Today`;
      } else if(dayDiff == 1) {
        return `1 day ago`
      } else if(dayDiff < 7) {
        return `${dayDiff} days ago`
      } else if(dayDiff < 14) {
        return `1 week ago`
      } else if(dayDiff < 30) {
        return `${Math.round(dayDiff/7)} weeks ago`
      } else if(dayDiff < 60) {
        return `1 month ago`
      } else if(dayDiff < 365) {
        return `${Math.round(dayDiff/30)} months ago`
      } else if(dayDiff < 730) {
        return `1 year ago`
      } else {
        return `${Math.round(dayDiff/365)} years ago`
      }
      
    },
    getTime() {
      if(this.data.time) {
        let time = this.data.time;
        let minutes = parseInt(time / 60);
        let seconds = time - minutes * 60;
        if(seconds < 10) { seconds = '0' + seconds }

        return `${minutes}:${seconds}`
      } else {
        return '';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.recording {
  width: 100%;
  height: 4rem;
  display: flex;
  justify-content: center;
  border-bottom: 2px solid #2526290e;

  .limiter {
    width: 100%;
    margin-left: 1.5rem;
    margin-right: 2rem;
    display: flex;
    flex-direction: row;

    @media (max-width: 650px) {
      margin-left: 0rem;
      margin-right: 0rem;
    }
  }
}

.watch-box {
  width: 4rem;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: 650px) {
    justify-content: center;
  }

  .circle {
    width: 2rem;
    height: 2rem;
    background-color: #252629cb;
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: 0.2s;

    @media (max-width: 650px) {
      width: 1.5rem;
      height: 1.5rem;
    }

    &:hover {
      cursor: pointer;
      opacity: 0.8;
    }

    .icon {
      font-size: 0.85rem;
      color: white;
      margin-left: 0.1rem;

      @media (max-width: 650px) {
        font-size: 0.6rem;
      }
    }
  }
}

.owner-box {
  width: 10rem;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: 1.2rem;
  padding-left: 1rem;
  color: #252629;

  @media (max-width: 650px) {
    font-size: 1rem;
    width: 8rem;
    padding-left: 0.5rem;
  }
}

.time-box {
  width: 5rem;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.1rem;
  color: #252629;

  @media (max-width: 650px) {
    display: none;
  }
}

.date-box {
  width: 10rem;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.1rem;
  color: #252629;

  @media (max-width: 650px) {
    font-size: 1rem;
    width: 8rem;
  }
}

.share-box {
  width: 3.5rem;
  height: 100%;
  margin-left: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #252629cb;
  font-size: 1.1rem;

  .icon {
    transition: 0.3s;
    &:hover {
      color: #11b6a5;
      cursor: pointer;
    }
  }
}

.delete-box {
  width: 3.5rem;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #252629cb;
  font-size: 1.1rem;

  .icon {
    transition: 0.3s;
    &:hover {
      color: #b61c11;
      cursor: pointer;
    }
  }
}
</style>